<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { getInboxClassByType } from 'dashboard/helper/inbox';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import AutomationMultiFileInput from 'dashboard/components/widgets/AutomationMultiFileInput.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    AutomationMultiFileInput,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edsubject: { type: String, default: '' },
    edfiles: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      subject: this.edsubject,
      content: this.edcontent,
      selectedInboxes: [],
      blobIds: (this.edfiles || []).map(f => f.blob_id),
      initialFilesForUploader: (this.edfiles || []).map(f => ({
        blob_id: f.blob_id,
        filename: f.filename,
        file_type: f.file_type,
        file_url: f.file_url,
        byte_size: f.byte_size || 0,
      })),
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    inboxes() {
      return this.$store.getters['inboxes/getActiveInboxes'];
    },
    currentCannedResponse() {
      return this.$store.getters.getCannedResponses.find(
        item => item.id === this.id
      );
    },
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  mounted() {
    if (this.currentCannedResponse?.inboxes) {
      this.selectedInboxes = this.currentCannedResponse.inboxes;
    }
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.subject = '';
      this.content = '';
      this.selectedInboxes = [];
      this.blobIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      const inboxIds = this.selectedInboxes.map(inbox => inbox.id);
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          subject: this.subject,
          content: this.content,
          inbox_ids: inboxIds,
          blob_ids: this.blobIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
    getInboxIcon(inbox) {
      return getInboxClassByType(inbox.channel_type, inbox.phone_number);
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" @close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.SUBJECT.LABEL') }}
            <input
              v-model="subject"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SUBJECT.PLACEHOLDER')"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.ATTACHMENTS.LABEL') }}
          </label>
          <AutomationMultiFileInput
            v-model="blobIds"
            :initial-files="initialFilesForUploader"
          />
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.INBOXES.LABEL') }}
          </label>
          <multiselect
            v-model="selectedInboxes"
            :options="inboxes"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            open-direction="top"
            hide-selected
            :placeholder="$t('CANNED_MGMT.EDIT.FORM.INBOXES.PLACEHOLDER')"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          >
            <template #option="{ option }">
              <div class="flex items-center gap-2">
                <fluent-icon
                  :icon="getInboxIcon(option)"
                  type="outline"
                  size="16"
                  class="text-slate-600 dark:text-slate-400"
                />
                <span>{{ option.internal_name || option.name }}</span>
              </div>
            </template>
            <template #tag="{ option }">
              <span class="multiselect__tag">
                <fluent-icon
                  :icon="getInboxIcon(option)"
                  type="outline"
                  size="14"
                  class="inline-block mr-1"
                />
                <span>{{ option.internal_name || option.name }}</span>
              </span>
            </template>
          </multiselect>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
